import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { queryKeys } from '@/lib/query-client';

import { api } from '../client';

export interface VenueMapping {
  id: string;
  location_id: string;
  location_name: string | null;
  venue_id: number;
  venue_name: string | null;
  is_active: boolean;
  source_system?: 'gopos' | 'pos';
  is_virtual?: boolean;
}

export interface OrderListItem {
  id: string;
  gopos_id: number | null;
  venue_mapping_id: string;
  venue_name: string | null;
  source_system?: 'gopos' | 'pos';
  /** POS: the server's per-store number, P-000123 / Z-000007 (MD-0107). */
  order_number: string | null;
  /** POS only: the fiscal printer's receipt number; it restarts at every Z. */
  fiscal_receipt_number?: string | null;
  fiscalized_at?: string | null;
  order_type: string | null;
  status: string | null;
  payment_status: string | null;
  payment_method: string | null;
  total_amount: string | null;
  sub_total_amount?: string | null;
  discount_total?: string;
  discount_name?: string | null;
  writeoff_total?: string;
  has_strata_payment?: boolean;
  currency: string;
  cash_amount?: string | null;
  card_amount?: string | null;
  sale_type?: 'SALE' | 'RETURN' | null;
  original_sale_id?: string | null;
  original_fiscal_receipt_number?: string | null;
  original_created_at?: string | null;
  original_order_number?: string | null;
  refund_method?: 'CASH' | 'CARD' | 'NONE' | null;
  payment_updated_at?: string | null;
  employee_name: string | null;
  terminal_name: string | null;
  created_at: string;
  closed_at: string | null;
  item_count: number;
}

export interface OrderItem {
  id: string;
  gopos_uid: string | null;
  product_id: string | null;
  product_name: string;
  quantity: string;
  stock_quantity_override?: string | null;
  stock_quantity_override_reason?: string | null;
  unit_price: string | null;
  total_price: string | null;
  sub_total_price?: string | null;
  discount_total?: string;
  writeoff_total?: string;
  tax_rate: string | null;
  is_removed: boolean;
  created_at: string | null;
}

export interface OrderTransaction {
  id: string;
  gopos_id: number | null;
  gopos_uid: string | null;
  type: string | null;
  status: string | null;
  amount: string | null;
  payment_method: string | null;
  paid_at: string | null;
  is_removed: boolean;
  /** MD-0111, POS payment legs: a cash leg's tender and change, a card leg's last 4 digits. */
  tendered?: string | null;
  change?: string | null;
  card_last4?: string | null;
}

export interface Order extends OrderListItem {
  gopos_uid: string | null;
  total_net: string | null;
  total_tax: string | null;
  sub_total_amount: string | null;
  total_paid_amount: string | null;
  transaction_total_amount: string | null;
  discount_total: string;
  discount_name?: string | null;
  writeoff_total: string;
  writeoff_count: number;
  has_strata_payment: boolean;
  service_charge: string;
  tip_total: string;
  rounding: string;
  public_link: string | null;
  cancellation_reason: string | null;
  pos_updated_at: string | null;
  synced_at: string;
  updated_at: string;
  items: OrderItem[];
  transactions: OrderTransaction[];
}

export interface OrdersFilter {
  venue_mapping_id?: string;
  date_from?: string;
  date_to?: string;
  order_number?: string;
  status?: string;
  payment_status?: string;
  limit?: number;
  offset?: number;
}

export interface OrdersSummary {
  total_amount: string;
  order_count: number;
  discount_total?: string;
  discounted_order_count?: number;
  writeoff_total?: string;
  writeoff_order_count?: number;
  currency: string;
}

export interface OrdersDaySummary {
  date: string;
  order_count: number;
  total_amount: string;
  discount_total?: string;
  discounted_order_count?: number;
  writeoff_total?: string;
  writeoff_order_count?: number;
  cash_amount?: string;
  card_amount?: string;
  currency: string;
}

export interface OrdersDaysResponse {
  items: OrdersDaySummary[];
  summary: OrdersSummary;
  data_start_date?: string | null;
  /** Warsaw days the list covers: without a requested start the server default window, or null (whole history) for a search. */
  date_from: string | null;
  date_to?: string | null;
}

export interface OrdersResponse {
  items: OrderListItem[];
  total: number;
  offset: number;
  limit: number;
  summary: OrdersSummary;
}

export interface OrderChangeEvent {
  id: string;
  order_id: string;
  venue_mapping_id: string;
  gopos_uid: string;
  order_number: string | null;
  change_type: string;
  message: string;
  is_highlight: boolean;
  details: Record<string, string | null> | null;
  display_at?: string | null;
  source_updated_at: string | null;
  created_at: string;
}

export interface OrderChangesResponse {
  items: OrderChangeEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface VenueMappingsResponse {
  items: VenueMapping[];
}

export const DEFAULT_ORDERS_LIMIT = 50;

async function fetchOrders(filters: OrdersFilter = {}): Promise<OrdersResponse> {
  const {
    venue_mapping_id,
    date_from,
    date_to,
    order_number,
    status,
    payment_status,
    limit = DEFAULT_ORDERS_LIMIT,
    offset = 0,
  } = filters;

  return api.webAdmin.get<OrdersResponse>('/gopos/orders', {
    params: {
      venue_mapping_id,
      date_from,
      date_to,
      order_number,
      status,
      payment_status,
      limit,
      offset,
    },
  });
}

async function fetchOrdersDays(filters: OrdersFilter = {}): Promise<OrdersDaysResponse> {
  const { venue_mapping_id, date_from, date_to, order_number, status, payment_status } = filters;

  return api.webAdmin.get<OrdersDaysResponse>('/gopos/orders/days', {
    params: {
      venue_mapping_id,
      date_from,
      date_to,
      order_number,
      status,
      payment_status,
    },
  });
}

async function fetchOrdersForDay(day: string, filters: OrdersFilter = {}): Promise<OrdersResponse> {
  const { venue_mapping_id, order_number, status, payment_status } = filters;

  return api.webAdmin.get<OrdersResponse>('/gopos/orders/day', {
    params: {
      day,
      venue_mapping_id,
      order_number,
      status,
      payment_status,
    },
  });
}

async function fetchOrder(id: string): Promise<Order> {
  return api.webAdmin.get<Order>(`/gopos/orders/${id}`);
}

async function fetchOrderChanges(
  filters: Pick<OrdersFilter, 'venue_mapping_id' | 'limit' | 'offset'> = {},
): Promise<OrderChangesResponse> {
  const { venue_mapping_id, limit = 50, offset = 0 } = filters;

  return api.webAdmin.get<OrderChangesResponse>('/gopos/orders/changes', {
    params: {
      venue_mapping_id,
      limit,
      offset,
    },
  });
}

async function fetchVenueMappings(
  options: { include_virtual_pos_locations?: boolean } = {},
): Promise<VenueMappingsResponse> {
  return api.webAdmin.get<VenueMappingsResponse>('/gopos/venue-mappings', {
    params: {
      include_virtual_pos_locations: options.include_virtual_pos_locations || undefined,
    },
  });
}

export interface UseGoposOrdersOptions {
  filters?: OrdersFilter;
  enabled?: boolean;
}

export function useGoposOrders(options: UseGoposOrdersOptions = {}) {
  const { filters = {}, enabled = true } = options;

  const query = useQuery({
    queryKey: queryKeys.gopos.orders(filters),
    queryFn: () => fetchOrders(filters),
    enabled,
  });

  return {
    orders: query.data?.items ?? [],
    summary: query.data?.summary ?? null,
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useGoposOrderDays(options: UseGoposOrdersOptions = {}) {
  const { filters = {}, enabled = true } = options;

  const query = useQuery({
    queryKey: queryKeys.gopos.orderDays(filters),
    queryFn: () => fetchOrdersDays(filters),
    enabled,
  });

  return {
    days: query.data?.items ?? [],
    summary: query.data?.summary ?? null,
    dataStartDate: query.data?.data_start_date ?? null,
    periodFrom: query.data?.date_from ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export interface UseGoposOrdersForDayOptions {
  day: string | null;
  filters?: OrdersFilter;
  enabled?: boolean;
}

export function useGoposOrdersForDay(options: UseGoposOrdersForDayOptions) {
  const { day, filters = {}, enabled = true } = options;

  const query = useQuery({
    queryKey: queryKeys.gopos.ordersForDay(day ?? '', filters),
    queryFn: () => {
      if (!day) throw new Error('Day is required');
      return fetchOrdersForDay(day, filters);
    },
    enabled: enabled && Boolean(day),
  });

  return {
    orders: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export interface UseGoposOrderOptions {
  id: string | null;
  enabled?: boolean;
}

export function useGoposOrder(options: UseGoposOrderOptions) {
  const { id, enabled = true } = options;

  const query = useQuery({
    queryKey: queryKeys.gopos.order(id ?? ''),
    queryFn: () => {
      if (!id) throw new Error('Order ID is required');
      return fetchOrder(id);
    },
    enabled: enabled && Boolean(id),
  });

  return {
    order: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export interface UseGoposOrderChangesOptions {
  filters?: Pick<OrdersFilter, 'venue_mapping_id' | 'limit' | 'offset'>;
  enabled?: boolean;
}

export function useGoposOrderChanges(options: UseGoposOrderChangesOptions = {}) {
  const { filters = {}, enabled = true } = options;

  const query = useQuery({
    queryKey: queryKeys.gopos.orderChanges(filters),
    queryFn: () => fetchOrderChanges(filters),
    enabled,
  });

  return {
    changes: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

const ORDER_CHANGES_PAGE_SIZE = 20;

export interface UseGoposOrderChangesInfiniteOptions {
  venueMappingId?: string | null;
  enabled?: boolean;
}

export function useGoposOrderChangesInfinite(options: UseGoposOrderChangesInfiniteOptions = {}) {
  const { venueMappingId, enabled = true } = options;

  const query = useInfiniteQuery({
    queryKey: queryKeys.gopos.orderChanges({ venue_mapping_id: venueMappingId ?? undefined }),
    queryFn: ({ pageParam = 0 }) => {
      const filters: Pick<OrdersFilter, 'venue_mapping_id' | 'limit' | 'offset'> = {
        limit: ORDER_CHANGES_PAGE_SIZE,
        offset: pageParam,
      };
      if (venueMappingId) filters.venue_mapping_id = venueMappingId;
      return fetchOrderChanges(filters);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const next = lastPage.offset + lastPage.limit;
      return next < lastPage.total ? next : undefined;
    },
    enabled,
  });

  return {
    changes: query.data?.pages.flatMap((p) => p.items) ?? [],
    total: query.data?.pages[0]?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  };
}

export function useGoposVenues(
  options: { enabled?: boolean; includeVirtualPosLocations?: boolean } = {},
) {
  const { enabled = true, includeVirtualPosLocations = false } = options;

  const query = useQuery({
    queryKey: queryKeys.gopos.venues({
      include_virtual_pos_locations: includeVirtualPosLocations,
    }),
    queryFn: () =>
      fetchVenueMappings({
        include_virtual_pos_locations: includeVirtualPosLocations,
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  return {
    venues: query.data?.items ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

const ordersApi = {
  fetchOrders,
  fetchOrder,
  fetchOrderChanges,
  fetchVenueMappings,
};

export default ordersApi;
